import React, {Component} from 'react';
import {connect} from 'react-redux';
import memoize from 'memoize-one';
import parse from 'html-react-parser';

import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent
} from 'primereact/dropdown';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import User, {
    YnPermissionName,
    GenericPermissionName
} from '~/shared/src/model/User';

import {
    getItemTypeDisplayName,
    getReferenceTypeDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {StoreState} from '~/frontend/src/store';
import {
    isInvalidHTML,
    stripHTMLtags
} from '~/frontend/src/utilities/valueRenderers';


type ReferenceDirection = 'src' | 'dest' | 'both';

const defaultProps = {

    /**
     * The ReferenceSetId of the selected ReferenceSet.
     */
    value: 0 as number,

    /**
     * The value to determine dirty against.
     */
    originalValue: 0 as number,

    /**
     * The first ItemTypeName of the ReferenceSet(s) selectable.
     */
    itemTypeName1: '' as string,

    /**
     * The second ItemTypeName of the ReferenceSet(s) selectable.
     */
    itemTypeName2: '' as string,

    /**
     * The ReferenceTypeName of the ReferenceSet(s) selectable.
     */
    referenceTypeName: '' as string,

    /**
     * The ReferenceTypeName of the ReferenceSet(s) selectable.
     */
    referenceDirection: 'both' as ReferenceDirection,

    /**
     * The permission to check for the ItemTypes displayed in the Dropdown.
     */
    permission: 'read' as YnPermissionName,

    /**
     * The generic permission to check for the ItemTypes displayed in the Dropdown.
     */
    genericPermission: 'none' as GenericPermissionName,

    /**
     * Should the field marked if dirty.
     */
    markDirty: false as boolean,

    /**
     * The label for the field.
     */
    label: '' as string,

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '' as string,

    inputStyle: { width: '100%' } as React.CSSProperties
};

type DefaultProps = Readonly<typeof defaultProps>;

type Props = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The database schema to show the ItemTypes for.
     */
    schema : Schema;

    /**
     * Label to be used for labeling ItemType1.
     */
    itemTypeLabel1? : string;

    /**
     * Label to be used for labeling ItemType2.
     */
    itemTypeLabel2? : string;

    /**
     * List of reference set ids to be included
     */
    includeReferenceSets? : number[],

    /**
     * List of reference set ids to be excluded
     */
    excludeReferenceSets? : number[],

    /**
     * true to leave dropdown enabled even if itemType1 and itemTyp2 are set. Used in AddTreeNodeDialog.
     * Prerequisite: includeReferenceSets must not be empty!
     */
    alwaysAllowSelect? : boolean,

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {String} itemTypeName
     */
    onChange : Function;

    /**
     * Callback to get alternate label for reference set
     *
     * @param {string} srcItemTypeName
     * @param {string} srcItemTypeLabel
     * @param {string} destItemTypeName
     * @param {string} destItemTypeLabel
     * @param {string} referenceTypeName
     * @param {string} referenceTypeLabel
     * @return {string|undefined}
     */
    onGetReferenceSetLabel? : ( srcItemTypeName : string, srcItemTypeLabel : string, destItemTypeName : string, destItemTypeLabel : string, referenceTypeName : string, referenceTypeLabel : string ) => string | undefined;

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    style? : React.CSSProperties;
};

export type ComponentProps = Omit<DropdownProps, 'onChange'> & Props & Partial<DefaultProps>;
type AllProps = Omit<DropdownProps, 'onChange'> & Props & DefaultProps & { dispatch : any };

type ReferenceSetData = {
    referenceSetId : number;
    referenceSetName : string;
    referenceSetLabel : string | React.ReactNode;
    referenceSetLabelRaw : string;
    isCircular : boolean;
}

/**
 * Dropdown for ItemType selection
 */
class ReferenceSetDropdown extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.getData = memoize( this.getData );

        this.onChange = this.onChange.bind( this );
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.checkValue();
    }

    /**
     * REACT lifecycle method
     */
    componentDidUpdate() {
        this.checkValue();
    }

    /**
     * gets the itemTypes for given schema
     *
     * @param {Schema} schema
     * @param {string} itemTypeName1
     * @param {string} itemTypeName2
     * @param {string} referenceTypeName
     * @param {ReferenceDirection} referenceDirection
     * @param {Boolean} rawMode
     * @param {Boolean} showAdditionalRawNames
     * @param {Permission} permission
     * @param {GenericPermission} genericPermission
     * @param {User} user
     *
     * @return {ItemTypeData[]}
     *
     * @private
     */
    getData( schema : Schema, itemTypeName1 : string, itemTypeName2 : string, referenceTypeName : string, referenceDirection : ReferenceDirection, itemTypeLabel1 : string | undefined, itemTypeLabel2 : string | undefined, rawMode : boolean, showAdditionalRawNames : boolean, permission : YnPermissionName, genericPermission : GenericPermissionName, user? : User ) : ReferenceSetData[] {
        const retVal : ReferenceSetData[]             = [],
              itemType1Id                             = metaModel[ schema ].getItemType( itemTypeName1 )?.id ?? 0,
              itemType2Id                             = metaModel[ schema ].getItemType( itemTypeName2 )?.id ?? 0,
              circularReferenceSetsHandled : number[] = [],
              {
                  includeReferenceSets,
                  excludeReferenceSets,
                  alwaysAllowSelect,
                  onGetReferenceSetLabel
              }                                       = this.props;

        let referenceSets : ReferenceSet[],
            reverseReferenceDirection : ReferenceDirection = referenceDirection;

        if ( alwaysAllowSelect && includeReferenceSets ) {
            referenceSets = includeReferenceSets.map( cirsId => metaModel[ schema ].getReferenceSet( cirsId ) ).filter( rs => rs !== undefined ) as ReferenceSet[];
        }
        else {
            if ( referenceDirection !== 'both' ) {
                if ( referenceDirection === 'src' ) {
                    reverseReferenceDirection = 'dest';
                }
                else {
                    reverseReferenceDirection = 'src';
                }
            }

            if ( !referenceTypeName ) {
                if ( itemTypeName1 && !itemTypeName2 ) {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemType( itemTypeName1, referenceDirection );
                }
                else if ( itemTypeName2 && !itemTypeName1 ) {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemType( itemTypeName2, reverseReferenceDirection );
                }
                else {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemTypes( itemTypeName1, itemTypeName2, referenceDirection );
                }
            }
            else {
                if ( itemTypeName1 && itemTypeName2 ) {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemTypesAndReferenceType( itemTypeName1, itemTypeName2, referenceTypeName, referenceDirection );
                }
                else if ( itemTypeName1 && !itemTypeName2 ) {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemTypeAndReferenceType( itemTypeName1, referenceTypeName, referenceDirection );
                }
                else if ( itemTypeName2 && !itemTypeName1 ) {
                    referenceSets = metaModel[ schema ].getReferenceSetsForItemTypeAndReferenceType( itemTypeName2, referenceTypeName, reverseReferenceDirection );
                }
                else {
                    referenceSets = metaModel[ schema ].getReferenceSetsForReferenceType( referenceTypeName );
                }
            }
        }

        for ( const referenceSet of referenceSets ) {
            if (
                referenceSet.id > 0
                && user?.hasPermissionOnReferenceSet( permission, referenceSet.id, schema )
                && ( genericPermission === 'none' || user?.hasGenericPermissionOnReferenceSet( genericPermission, referenceSet.id, schema ) )
                && ( !includeReferenceSets || includeReferenceSets.includes( referenceSet.id ) )
                && ( !excludeReferenceSets || excludeReferenceSets.includes( referenceSet.id ) )
            ) {
                const referenceTypeLabel = getReferenceTypeDisplayName( referenceSet.id, schema, rawMode, showAdditionalRawNames );

                let srcItemTypeLabel                             = getItemTypeDisplayName( referenceSet.srcItemTypeId, schema, rawMode, showAdditionalRawNames ),
                    destItemTypeLabel                            = getItemTypeDisplayName( referenceSet.destItemTypeId, schema, rawMode, showAdditionalRawNames ),
                    referenceSetLabel : string | React.ReactNode = '';

                // circular
                if ( itemTypeName1 === itemTypeName2 ) {
                    if ( !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                        srcItemTypeLabel = itemTypeLabel1 ?? srcItemTypeLabel;
                    }
                    else {
                        destItemTypeLabel = itemTypeLabel2 ?? destItemTypeLabel;
                    }
                }
                else {
                    if ( referenceSet.srcItemTypeId === itemType1Id && itemTypeLabel1 && !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                        srcItemTypeLabel = itemTypeLabel1;
                    }
                    else if ( referenceSet.srcItemTypeId === itemType2Id && itemTypeLabel2 ) {
                        srcItemTypeLabel = itemTypeLabel2;
                    }

                    if ( referenceSet.destItemTypeId === itemType1Id && itemTypeLabel1 && circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                        destItemTypeLabel = itemTypeLabel1;
                    }
                    else if ( referenceSet.destItemTypeId === itemType2Id && itemTypeLabel2 ) {
                        destItemTypeLabel = itemTypeLabel2;
                    }
                }

                const referenceSetLabelRaw = onGetReferenceSetLabel?.(
                        metaModel[ schema ].getItemType( referenceSet.srcItemTypeId )!.name,
                        srcItemTypeLabel,
                        metaModel[ schema ].getItemType( referenceSet.destItemTypeId )!.name,
                        destItemTypeLabel,
                        referenceSet.referenceTypeName,
                        referenceTypeLabel
                    )
                    ?? `${srcItemTypeLabel} ${__( 'YN.base.finder.reference.directionMarker' )} ${referenceTypeLabel} ${__( 'YN.base.finder.reference.directionMarker' )} ${destItemTypeLabel}`;

                if ( !isInvalidHTML( referenceSetLabelRaw ) ) {
                    referenceSetLabel = parse( referenceSetLabelRaw );
                }
                else {
                    referenceSetLabel = referenceSetLabelRaw;
                }

                retVal.push( {
                    referenceSetId  : circularReferenceSetsHandled.includes( referenceSet.id ) ? -referenceSet.id : referenceSet.id,
                    referenceSetName: referenceSet.name,
                    referenceSetLabel,
                    referenceSetLabelRaw,
                    isCircular      : referenceSet.srcItemTypeId === referenceSet.destItemTypeId
                } );

                if ( referenceSet.srcItemTypeId === referenceSet.destItemTypeId && !circularReferenceSetsHandled.includes( referenceSet.id ) ) {
                    circularReferenceSetsHandled.push( referenceSet.id )
                }
            }
        }

        retVal.sort( ( val1, val2 ) => {
            const referenceSetLabelRaw1 = !isInvalidHTML( val1.referenceSetLabelRaw )
                      ? stripHTMLtags( val1.referenceSetLabelRaw )
                      : val1.referenceSetLabelRaw,
                  referenceSetLabelRaw2 = !isInvalidHTML( val2.referenceSetLabelRaw )
                      ? stripHTMLtags( val2.referenceSetLabelRaw )
                      : val2.referenceSetLabelRaw;

            if ( referenceSetLabelRaw1 > referenceSetLabelRaw2 ) {
                return 1;
            }
            else if ( referenceSetLabelRaw1 < referenceSetLabelRaw2 ) {
                return -1;
            }
            else {
                return 0;
            }
        } );

        return retVal;
    }

    checkValue() {
        const {
                  schema,
                  itemTypeName1,
                  itemTypeName2,
                  referenceTypeName,
                  referenceDirection,
                  itemTypeLabel1,
                  itemTypeLabel2,
                  rawMode,
                  showAdditionalRawNames,
                  value,
                  permission,
                  genericPermission,
                  user
              } = this.props as AllProps;

        if ( !value ) {
            const data = this.getData( schema, itemTypeName1, itemTypeName2, referenceTypeName, referenceDirection, itemTypeLabel1, itemTypeLabel2, rawMode, showAdditionalRawNames, permission, genericPermission, user );

            if ( data && data?.length === 1 ) {
                this.onChange( { value: data[ 0 ] } as DropdownChangeEvent );
            }
        }
    }

    /**
     * Handler for Dropdown change event
     *
     * @private
     */
    onChange( event : DropdownChangeEvent ) {
        let value = 0;

        if ( event.value ) {
            value = event.value.referenceSetId;
        }

        this.props.onChange( value );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        // order by ItemTypeName
        const {
                  schema,
                  itemTypeName1,
                  itemTypeName2,
                  referenceTypeName,
                  referenceDirection,
                  itemTypeLabel1,
                  itemTypeLabel2,
                  rawMode,
                  showAdditionalRawNames,
                  value: rawValue,
                  originalValue,
                  markDirty,
                  label,
                  uid,
                  className,
                  style,
                  inputStyle,
                  permission,
                  genericPermission,
                  user,
                  // onChange is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onChange,
                  // includeReferenceSets is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  includeReferenceSets,
                  // alwaysAllowSelect is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  alwaysAllowSelect,
                  // onGetReferenceSetLabel is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onGetReferenceSetLabel,
                  // dispatch is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  dispatch,
                  ...dropdownProps
              }                  = this.props as AllProps,
              data               = this.getData( schema, itemTypeName1, itemTypeName2, referenceTypeName, referenceDirection, itemTypeLabel1, itemTypeLabel2, rawMode, showAdditionalRawNames, permission, genericPermission, user ),
              value              = data
                  ? ( data.length === 1 ? data[ 0 ] : data.find( ( rec : ReferenceSetData ) => rec.referenceSetId === rawValue ) ?? '' )
                  : '',
              dropdownClassNames = [ 'yn-inputgroup' ];

        if ( !label ) {
            dropdownClassNames.push( className );
        }
        if ( markDirty && rawValue !== originalValue ) {
            dropdownClassNames.push( 'yn-dirty-border' );
        }

        return <div className={className + ' field'} style={style}>
            {label
                ? <label
                    htmlFor={`${uid}_ReferenceSetDropdown`}
                    className="block"
                >
                    {label}
                </label>
                : ''
            }
            <Dropdown
                inputId={`${uid}_ReferenceSetDropdown`}
                value={value}
                onChange={this.onChange}
                options={data}
                optionLabel="referenceSetLabel"
                filter
                filterBy="referenceSetLabel"
                disabled={data.length < 2}
                showClear={data.length > 1}
                className={dropdownClassNames.join( ' ' )}
                placeholder="[ any ]"
                style={inputStyle}
                {...dropdownProps}
            />
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : Partial<ComponentProps> {
    const retVal : Partial<ComponentProps> = {};

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

// typing of the connect function makes working that the action props are defined but not required when using the component
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect<Partial<ComponentProps>, {}, ComponentProps, StoreState>( mapStateToProps )( ReferenceSetDropdown );
